import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Ban, ClipboardCopy, PackageCheck, Plus, Printer, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { PageHeader } from "@/components/AdminLayout";
import { Pill } from "@/components/Badges";
import { NewPODialog } from "@/components/NewPODialog";
import { ReceivePODialog } from "@/components/ReceivePODialog";
import { VendorsPanel } from "@/components/VendorsPanel";
import { api, errMsg, fmtDate } from "@/lib/api";
import { poText, printPoSheet } from "@/lib/poSheet";

const STATUS = { open: ["amber", "Waiting"], partial: ["sky", "Partly arrived"], received: ["emerald", "Received"], cancelled: ["slate", "Cancelled"] };
const FILTERS = [["waiting", "Waiting"], ["received", "Received"], ["cancelled", "Cancelled"], ["all", "All"]];
const matches = (po, f) => f === "all" || (f === "waiting" ? ["open", "partial"].includes(po.status) : po.status === f);

function POActions({ po, onReceive, onConfirm }) {
  const live = ["open", "partial"].includes(po.status);
  const none = po.items.every((it) => it.received_boxes === 0);
  const copy = () => {
    if (!navigator.clipboard) return toast.error("Copying isn't available in this browser");
    navigator.clipboard.writeText(poText(po)).then(() => toast.success("Order copied. Paste it in a message or email."), () => toast.error("Couldn't copy. Try Print sheet instead."));
  };
  const print = () => !printPoSheet(po) && toast.error("Allow pop-ups to print the order sheet");
  return (
    <div className="flex flex-wrap gap-2">
      <Button size="sm" variant="outline" className="gap-1.5" onClick={print} data-testid={`po-print-${po.po_number}`}><Printer className="h-3.5 w-3.5" />Print sheet</Button>
      <Button size="sm" variant="outline" className="gap-1.5" onClick={copy} data-testid={`po-copy-${po.po_number}`}><ClipboardCopy className="h-3.5 w-3.5" />Copy text</Button>
      {live && <Button size="sm" className="gap-1.5" onClick={() => onReceive(po)} data-testid={`po-receive-${po.po_number}`}><PackageCheck className="h-3.5 w-3.5" />Receive</Button>}
      {live && <Button size="sm" variant="ghost" className="gap-1.5 text-muted-foreground" onClick={() => onConfirm({ po, kind: "cancel" })} data-testid={`po-cancel-${po.po_number}`}><Ban className="h-3.5 w-3.5" />Cancel</Button>}
      {none && <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => onConfirm({ po, kind: "delete" })} data-testid={`po-delete-${po.po_number}`}><Trash2 className="h-4 w-4 text-red-400" /></Button>}
    </div>
  );
}

function POCard({ po, onReceive, onConfirm }) {
  const [c, label] = STATUS[po.status];
  return (
    <div className="rounded-lg border border-border bg-card p-5" data-testid={`po-card-${po.po_number}`}>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="flex items-center gap-3 font-mono-data text-lg">{po.po_number} <Pill color={c} testId={`po-status-${po.po_number}`}>{label}</Pill></p>
          <p className="text-sm text-muted-foreground">{po.vendor_name} · ordered {fmtDate(po.created_at)}{po.received_at && ` · received ${fmtDate(po.received_at)}`}</p>
        </div>
        <POActions po={po} onReceive={onReceive} onConfirm={onConfirm} />
      </div>
      <div className="divide-y divide-border/60 rounded-md border border-border/60">
        {po.items.map((it) => {
          const left = it.boxes - it.received_boxes;
          return (
            <div key={it.product_id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm" data-testid={`po-item-${po.po_number}-${it.product_id}`}>
              <span>{it.name} <span className="font-mono-data text-xs text-muted-foreground">{it.vial_size}</span></span>
              <span className="font-mono-data text-xs">
                {it.received_boxes}/{it.boxes} box(es) arrived
                {left > 0 && po.status !== "cancelled" && <span className="ml-2 text-amber-400">{left} waiting</span>}
              </span>
            </div>
          );
        })}
      </div>
      {po.notes && <p className="mt-3 text-xs text-muted-foreground">Notes: {po.notes}</p>}
    </div>
  );
}

export default function VendorOrders() {
  const [pos, setPos] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [filter, setFilter] = useState("waiting");
  const [formOpen, setFormOpen] = useState(false);
  const [receiving, setReceiving] = useState(null);
  const [tab, setTab] = useState("orders");
  const [loaded, setLoaded] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/purchase-orders").then((r) => setPos(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/admin/vendors").then((r) => { setVendors(r.data); setLoaded(true); }).catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const act = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const runConfirm = () => {
    const { po, kind } = confirm;
    setConfirm(null);
    act(() => (kind === "cancel" ? api.post(`/admin/purchase-orders/${po.id}/cancel`) : api.delete(`/admin/purchase-orders/${po.id}`)), kind === "cancel" ? "Order cancelled" : "Order deleted");
  };
  const onSaved = () => {
    setTab("orders");
    setFilter("waiting");
    load();
  };
  const shown = useMemo(() => pos.filter((p) => matches(p, filter)), [pos, filter]);
  const waitingCount = pos.filter((p) => matches(p, "waiting")).length;
  const newOrder = () => {
    if (!loaded) return;
    if (vendors.length) return setFormOpen(true);
    toast.info("Add a vendor first");
    setTab("vendors");
  };

  return (
    <div>
      <PageHeader eyebrow="Restock" title="Vendor orders">
        <Button className="gap-2" onClick={newOrder} disabled={!loaded} data-testid="po-new-button"><Plus className="h-4 w-4" />New vendor order</Button>
      </PageHeader>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="orders" data-testid="po-tab-orders">Orders{waitingCount > 0 && ` (${waitingCount} waiting)`}</TabsTrigger>
          <TabsTrigger value="vendors" data-testid="po-tab-vendors">Vendors</TabsTrigger>
        </TabsList>
        <TabsContent value="orders" className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {FILTERS.map(([k, l]) => (
              <button key={k} onClick={() => setFilter(k)} data-testid={`po-filter-${k}`}
                className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${filter === k ? "border-emerald-500 bg-emerald-500/10 text-emerald-300" : "border-border text-muted-foreground hover:text-foreground"}`}>{l}</button>
            ))}
          </div>
          {shown.map((po) => <POCard key={po.id} po={po} onReceive={setReceiving} onConfirm={setConfirm} />)}
          {shown.length === 0 && <p className="rounded-lg border border-border bg-card py-10 text-center text-sm text-muted-foreground" data-testid="po-empty">No vendor orders here yet.</p>}
        </TabsContent>
        <TabsContent value="vendors"><VendorsPanel vendors={vendors} reload={load} /></TabsContent>
      </Tabs>
      <NewPODialog open={formOpen} onClose={() => setFormOpen(false)} onSaved={onSaved} vendors={vendors} />
      <ReceivePODialog po={receiving} onClose={() => setReceiving(null)} onSaved={load} />
      <AlertDialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm?.kind === "cancel" ? `Cancel ${confirm?.po.po_number}?` : `Delete ${confirm?.po.po_number}?`}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.kind === "cancel" ? "Boxes still waiting will no longer show as coming. Anything already received stays in stock." : "This order sheet will be removed from your history."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="po-confirm-keep">Keep it</AlertDialogCancel>
            <AlertDialogAction onClick={runConfirm} data-testid="po-confirm-yes">{confirm?.kind === "cancel" ? "Cancel order" : "Delete"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
